import React, { useEffect, useState } from 'react';
import { CocoEvent } from '@cotera/client/app/etc';
import { z } from 'zod/v3';
import type { QueryClient } from '@tanstack/react-query';
import { err, ok } from 'neverthrow';
import {
  ActionResult,
  ActionTrackMetadata,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
} from '@cotera/client/v0/actions/framework';
import type { AskResponse } from '@cotera/client/v0/actions/framework/types';
import { Assert } from '@cotera/utilities';
import { ModalPageView } from '@cotera/client/app/components/app';
import { Loading, toast } from '@cotera/client/app/components/ui';
import { Button } from '@cotera/client/v0/components/ui/button';
import { Input } from '@cotera/client/v0/components/ui/input';
import { queryKeys } from '@cotera/client/app/hooks/query-cache-keys';
import { ResourceType } from '@cotera/api';
import { WorkflowModel } from './workflow.model';
import { DEFAULT_NEW_WORKFLOW_NAME } from './workflow-draft-graph';
import { isSaveArtifactsApplicable } from '@cotera/client/v0/chat/artifact-dirty-state';

const saveWorkflowParamsSchema = z.object({
  folderId: z.string().uuid().optional(),
  name: z.string().min(1).optional(),
  silent: z.boolean().optional(),
  /**
   * Resolve the model to save by id (multi-artifact save) instead of the
   * targeted one. Falls back to targeted when unset.
   */
  workflowId: z.string().optional(),
});

type Params = z.infer<typeof saveWorkflowParamsSchema>;

type ModalManager = {
  push: (props: { view: () => React.ReactNode; onClose?: () => void }) => void;
  pop: () => void;
};

export class SaveWorkflowAction extends BaseAction<
  Params,
  { t: string; message?: string }[]
> {
  title = 'Save Workflow';
  description = 'Save workflow changes';
  inputSchema = saveWorkflowParamsSchema;
  shortcut = ['ctrl', 's'];
  group = 'Workflow';
  icon = 'save';

  private _lastSavedWorkflowId: string | null = null;
  private _lastSaveWasNew: boolean = false;
  /** Navigation saves pass silent:true; we don't track those. */
  private _lastSaveWasSilent: boolean = false;

  constructor(
    private readonly queryClient: QueryClient,
    private readonly modalManager: ModalManager
  ) {
    super();
  }

  applicable(context: ApplicableContext): boolean {
    // Chat-level Save Artifacts owns Ctrl+S when any open artifact is dirty.
    if (isSaveArtifactsApplicable(context)) {
      return false;
    }
    const model = context.targetedModelOfType(WorkflowModel);
    return (
      model !== null && model.hasUnsavedChangesWatchable.snapshot() === true
    );
  }

  shouldAsk(context: ApplicableContext): boolean {
    const model = context.targetedModelOfType(WorkflowModel);
    if (model === null || !model.isNew) {
      return false;
    }
    return model.getNameForSave() === DEFAULT_NEW_WORKFLOW_NAME;
  }

  ask(
    params: Partial<Params>,
    context: ExecuteContext
  ): AskResponse<Params, { t: string; message?: string }[]> {
    const model = context.targetedModelOfType(WorkflowModel);
    Assert.assert(model !== null, 'WorkflowModel not found');

    const initialName = params.name ?? model.getNameForSave();

    return {
      view: ({ onRespond, onCancel }) => {
        const handleCancel = async () => {
          await onCancel();
          this.modalManager.pop();
        };

        this.modalManager.push({
          view: () => (
            <SaveWorkflowAskView
              initialName={initialName}
              onRespond={async (nextParams) => {
                await onRespond(nextParams);
                this.modalManager.pop();
              }}
              onCancel={handleCancel}
            />
          ),
          onClose: handleCancel,
        });
      },
    };
  }

  async execute(
    params: Params,
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    const model =
      params.workflowId !== undefined
        ? context.getInScopeModelOfType(WorkflowModel, params.workflowId)
        : context.targetedModelOfType(WorkflowModel);
    if (model === null) {
      return ok({});
    }

    this._lastSaveWasNew = model.isNew;
    const silent = params.silent === true;
    this._lastSaveWasSilent = silent;
    const saveToast = silent
      ? null
      : toast.loading('Saving workflow...', {
          id: 'save-workflow',
        });

    try {
      const workflowId = model.workflowId;
      const resolvedName = (params.name ?? model.getNameForSave()).trim();
      if (resolvedName === '') {
        const message = 'Workflow name is required';
        saveToast?.error(message);
        return err([{ t: 'error', message }]);
      }

      model.setNameForSave(resolvedName);

      const persistResult = await model.persistDraft({
        name: resolvedName,
        ...(params.folderId !== undefined && { folderId: params.folderId }),
      });
      if (!persistResult.success) {
        saveToast?.error(persistResult.message);
        return err([{ t: 'error', message: persistResult.message }]);
      }

      this._lastSavedWorkflowId = workflowId;

      saveToast?.success('Workflow saved');

      const resource = model.resourceWatchable.snapshot();
      const orgId = resource?.workflow.orgId;
      if (orgId !== undefined) {
        const resourceQueryKey = queryKeys.resource.get({
          orgId,
          type: ResourceType.Workflow,
          id: workflowId,
        });
        await this.queryClient.invalidateQueries({
          queryKey: resourceQueryKey,
        });
      }

      return ok({});
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to save workflow';
      saveToast?.error(message);
      return err([{ t: 'error', message }]);
    }
  }

  track(): ActionTrackMetadata | null {
    // Skip navigation saves — only track deliberate user saves.
    if (this._lastSavedWorkflowId === null || this._lastSaveWasSilent) {
      return null;
    }
    return {
      event: CocoEvent.WorkflowSaved,
      properties: {
        workflowId: this._lastSavedWorkflowId,
        isNew: this._lastSaveWasNew,
      },
    };
  }
}

const SaveWorkflowAskView: React.FC<{
  initialName: string;
  onRespond: (params: Params) => Promise<void>;
  onCancel: () => Promise<void>;
}> = ({ initialName, onRespond, onCancel }) => {
  const [name, setName] = useState(initialName);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setName(initialName);
  }, [initialName]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (trimmed.length === 0 || loading) {
      return;
    }
    setLoading(true);
    await onRespond({ name: trimmed });
    setLoading(false);
  };

  return (
    <ModalPageView
      title="Save workflow"
      subtitle="Choose a name for this workflow"
      className="px-4 py-4"
    >
      <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
        <div className="flex flex-col gap-2">
          <label htmlFor="workflow-name" className="text-sm font-medium">
            Workflow name
          </label>
          <Input
            id="workflow-name"
            placeholder="Workflow name"
            value={name}
            autoFocus
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => void onCancel()}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="default"
            icon="save"
            disabled={loading || name.trim().length === 0}
          >
            {loading ? <Loading.Spinner variant="sm" /> : 'Save'}
          </Button>
        </div>
      </form>
    </ModalPageView>
  );
};
